import React, { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Button, Form } from '~/shared/components';

import { useTranslation } from '~/shared/utils/translation';
import { useSubmit } from '../../hooks/useSubmit';
import { DeliveryAddressForm, Fields } from '../../models/fields';

import { useValidationSchema } from './lib/useValidationSchema';
import { useBasket } from '~/features/basket';
import { FormWrapper, StyledEditButton } from './styled';
import { useCheckout } from '../..';

import { useCityByPostCode } from '~/shared/hooks/useCitySearch/useCityByPostCode';
import { SavedAddress } from './components/SavedAddress';
import { EditAddressForm } from './components/EditAddressForm';
import { useCheckoutState } from '../../hooks/useCheckoutState';
import Flex from '~/shared/components/Flex';

type Props = {
    selectAddress?: () => void;
};

export const DeliveryAddressSummary = ({ selectAddress }: Props) => {
    const { translate } = useTranslation();
    const { billingAddress = {} } = useBasket();
    const validationSchema = useValidationSchema();
    const { handlers } = useSubmit();

    const { setAlternateShippingValid, setUsingAlternateDeliveryAddress } = useCheckout();

    const { setAlternateShippingAddress, isFormInEditMode, setIsFormInEditMode } =
        useCheckoutState();

    const methods = useForm<DeliveryAddressForm>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            SameDeliveryAddress: !!billingAddress,
        },
    });

    const sameDeliveryAddress = methods.watch(Fields.SameDeliveryAddress);

    const isValid = methods.formState.isValid;
    const zipCode = methods.watch(Fields.PostalCode);
    const { cityName, hasNoMatchingPostCode, isFetchingCityByPostCode } =
        useCityByPostCode(zipCode);

    useEffect(() => {
        if (isFetchingCityByPostCode) {
            methods.setValue(Fields.City, '');
            return;
        }

        if (cityName) {
            methods.setValue(Fields.City, cityName);
            methods.clearErrors(Fields.PostalCode);
            return;
        }

        if (hasNoMatchingPostCode) {
            methods.setError(Fields.PostalCode, {
                message: translate('message.postNumberValidationError.notFound'),
            });
            methods.setValue(Fields.City, '');
            methods.setValue(Fields.PostalCode, '');
        }
    }, [cityName, hasNoMatchingPostCode, isFetchingCityByPostCode]);

    useEffect(() => {
        setAlternateShippingValid(isValid);
    }, [isValid]);

    useEffect(() => {
        if (sameDeliveryAddress) {
            methods.reset();
            methods.trigger();
        }

        setUsingAlternateDeliveryAddress(!sameDeliveryAddress);
    }, [sameDeliveryAddress]);

    const allRequiredFieldsIsValid = useMemo(() => {
        const requiredFields = [
            Fields.FirstName,
            Fields.LastName,
            Fields.Address1,
            Fields.PostalCode,
            'phone',
        ];
        return requiredFields.every((key) => Object.keys(billingAddress).includes(key));
    }, [billingAddress]);

    const handleSaveAddress = () => {
        const values = methods.getValues();
        setAlternateShippingAddress(values);
        setIsFormInEditMode(false);
    };

    useEffect(() => {
        if (billingAddress && !allRequiredFieldsIsValid) {
            methods.setValue(Fields.FirstName, String(billingAddress.firstName));
            methods.setValue(Fields.LastName, String(billingAddress.lastName));
            methods.setValue(Fields.Address1, billingAddress.address1 ?? '');
            methods.setValue(Fields.PostalCode, billingAddress.postalCode ?? '');
            methods.setValue(Fields.City, billingAddress.city ?? '');
            methods.setValue(Fields.Phone, Number(billingAddress.phone));

            methods.setValue(Fields.SameDeliveryAddress, false);

            methods.trigger();
            setUsingAlternateDeliveryAddress(true);
        }
        setIsFormInEditMode(false);
    }, []);

    return (
        <div style={{ width: '100%', position: 'relative' }}>
            <Flex>
                {isFormInEditMode && (
                    <StyledEditButton
                        tabIndex={0}
                        onClick={() => {
                            methods.setValue(Fields.SameDeliveryAddress, true);
                            setIsFormInEditMode(false);
                        }}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                methods.setValue(Fields.SameDeliveryAddress, true);
                                setIsFormInEditMode(false);
                            }
                        }}
                    >
                        {translate('checkout.delivery.goBack')}
                    </StyledEditButton>
                )}
                {!isFormInEditMode && (
                    <StyledEditButton
                        tabIndex={0}
                        data-testid="address-delivery-correct"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => {
                            methods.setValue(Fields.SameDeliveryAddress, false);
                            setIsFormInEditMode(true);
                        }}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                methods.setValue(Fields.SameDeliveryAddress, false);
                                setIsFormInEditMode(true);
                            }
                        }}
                    >
                        {translate('checkout.delivery.correctAddress')}
                    </StyledEditButton>
                )}
            </Flex>

            <Form methods={methods} onSubmit={(payload) => handlers.submitShippingAddress(payload)}>
                <FormWrapper>
                    <div>
                        {allRequiredFieldsIsValid && !isFormInEditMode ? (
                            <Flex alignItems="flex-end" justifyContent="space-between" gap="small">
                                <SavedAddress
                                    billingAddress={billingAddress}
                                    sameDeliveryAddress={sameDeliveryAddress}
                                    methods={methods}
                                />

                                {selectAddress && (
                                    <Button
                                        aria-label="Gem valg af adresse"
                                        data-testid="address-delivery-save"
                                        type="button"
                                        small
                                        onClick={selectAddress}
                                    >
                                        {translate('checkout.delivery.save')}
                                    </Button>
                                )}
                            </Flex>
                        ) : (
                            <EditAddressForm />
                        )}

                        {isFormInEditMode && (
                            <Button
                                disabled={!isValid}
                                data-testid="save-custom-address"
                                size="large"
                                onClick={handleSaveAddress}
                            >
                                {translate('checkout.delivery.save')}
                            </Button>
                        )}
                    </div>
                </FormWrapper>
            </Form>
        </div>
    );
};
